import {Component, computed, effect, input, output, ChangeDetectionStrategy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeChipModule} from 'se-fe-chip';
import {SeFeEmptyStateModule} from 'se-fe-empty-state';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeHeaderModule} from 'se-fe-header';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeMenuModule} from 'se-fe-menu';
import {SeFeModalModule} from 'se-fe-modal';
import {SeFePageModule} from 'se-fe-page';
import {SeFePositionModule} from 'se-fe-position';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {SeFeToastModule} from 'se-fe-toast';
import {ItemOrgModalComponent} from './item-org-modal/item-org-modal.component';
import {
  ProductOrganizationUpdate,
  UpdateProductDetail,
} from '../../../features/product/components/product-detail/product-detail.store';
import {SeFeOverlayModule} from 'se-fe-overlay';
import {CatalogSummary, Product} from '../../../models/clare';

@Component({
  selector: 'item-org-card',
  imports: [
    CommonModule,
    ItemOrgModalComponent,
    ReactiveFormsModule,
    RouterLink,
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeChipModule,
    SeFeEmptyStateModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeGridModule,
    SeFeHeaderModule,
    SeFeIconModule,
    SeFeMenuModule,
    SeFeModalModule,
    SeFeOverlayModule,
    SeFePageModule,
    SeFePositionModule,
    SeFeSpinnerModule,
    SeFeToastModule,
  ],
  templateUrl: './item-org-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './item-org-card.component.scss',
})
export class ItemOrgCardComponent {
  cardTitle = input<string>('Product organization');
  brandName = input<string>();
  vendorName = input<string>();
  taxCode = input<string | undefined>();
  catalogs = input<CatalogSummary[]>([]);
  catalogsLoading = input(false);
  organizationCatalogs = input<CatalogSummary[]>([]);
  organizationCatalogsLoading = input(false);
  organizationId = input('');
  products = input<Product[]>();
  editable = input<boolean>(false);
  disabledFields = input<boolean>(false);
  catalogsOnly = input(false);
  organizationCatalogsRequested = output<void>();
  updateProductBrand = output<UpdateProductDetail<ProductOrganizationUpdate>>();
  productBrand = computed(() => ({label: this.uniqueBrands().length > 1 ? 'Brands' : 'Brand'}));
  productBrands = {label: 'Brands'};
  productTaxCode = {label: 'Tax Code'};
  productCatalogs = {label: 'Catalogs'};
  productVendor = {label: 'Vendor'};
  disabledFieldsForm = new FormGroup({
    brands: new FormControl({value: '', disabled: true}, {nonNullable: true}),
    vendor: new FormControl({value: '', disabled: true}, {nonNullable: true}),
  });

  selectedCatalogIds = computed(() => this.catalogs().map(catalog => String(catalog.id)));

  private uniqueBrands = computed(() => {
    const products = this.products() ?? [];

    return [
      ...new Set(
        products
          .map(product => product.display_metadata?.brand)
          .filter((brand): brand is string => typeof brand === 'string' && brand.trim().length > 0)
          .map(brand => brand.trim())
      ),
    ];
  });

  /**
   * Computes a comma-separated list of unique product brands from the products input.
   * Returns '--' if no valid brands are found.
   * Uses a computed signal for memoization.
   */
  computedBrands = computed((): string => {
    const brands = this.uniqueBrands();

    return brands.length > 0 ? brands.join(', ') : '--';
  });

  private syncDisabledFields = effect(() => {
    if (!this.disabledFields()) {
      return;
    }

    this.disabledFieldsForm.controls.brands.setValue(this.computedBrands(), {emitEvent: false});
    this.disabledFieldsForm.controls.vendor.setValue(this.vendorName() || '--', {emitEvent: false});
  });

  onUpdateProductBrand(event: UpdateProductDetail<ProductOrganizationUpdate>): void {
    this.updateProductBrand.emit(event);
  }

  onOrganizationCatalogsRequested(): void {
    this.organizationCatalogsRequested.emit();
  }
}
